import { SearchInputCommand } from "@/components/inputs/search-input-command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEffect } from "react";
import { debounce } from "@/utils/debounce";
import { Controller, useForm } from "react-hook-form";
import { useList } from "../../context/use-list";
import { FilterPrivateOfficeContractsFormData } from "./filters-private-office-contracts.types";
import { useCompanySelection } from "@/hooks/useCompanySelection";
import { ComboboxDemo } from "@/components/ui/combobox";
import { ChevronDownIcon } from "lucide-react";
import { useSpaceSelection } from "@/hooks/useSpaceSelection";
import { statusOptions } from "../../utils/private-office-contract";
import { useSearchParams } from "react-router-dom";

export const FiltersPrivateOfficeContracts = () => {
  const { companies, handleSearchCompanies } = useCompanySelection({});
  const { spaces, handleSearchSpaces } = useSpaceSelection({});
  const {
    isLoading,
    updateFilters,
    clearFilters: clearListFilters,
  } = useList();
  const [searchParams] = useSearchParams();

  const { control, handleSubmit, reset, watch, setValue, getValues } =
    useForm<FilterPrivateOfficeContractsFormData>({
      mode: "onBlur",
      reValidateMode: "onChange",
      defaultValues: {
        company: "",
        space: "",
        poContractId: searchParams.get("po_contract_id") || "",
        startDate: "",
        endDate: "",
        status: {
          id: "",
          name: "",
        },
      },
    });

  const handleSelectCompany = (item: string) => {
    setValue("company", item);
  };

  const handleSelectSpace = (item: string) => {
    setValue("space", item);
  };

  const handleSelectPeriod = (
    nameKey: "startDate" | "endDate",
    item: string,
  ) => {
    setValue(nameKey, item);
  };

  const handleSelectStatus = (item: { id: number; name: string }) => {
    setValue("status", {
      id: item.id.toString(),
      name: item.name,
    });
  };

  const clearFilters = () => {
    reset({
      space: undefined,
      poContractId: "",
      company: undefined,
      startDate: "",
      endDate: "",
      status: {
        id: "",
        name: "",
      },
    });
    clearListFilters();
  };

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name !== "poContractId") {
        handleSubmit(onSubmit)();
      }
    });
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const onSubmit = (data: FilterPrivateOfficeContractsFormData) => {
    updateFilters({
      space: data.space,
      company: data.company,
      poContractId: data.poContractId,
      startDate: data.startDate,
      endDate: data.endDate,
      status: data.status?.id,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="bg-white px-8 pt-0 pb-4 mt-5 border-b">
        <div className="flex items-end justify-between">
          <div className="flex gap-5 flex-col w-full">
            <div className="flex gap-4 items-end w-full">
              <div className="w-1/1 text-sm font-bold flex items-center h-10">
                Filtros:
              </div>
              <div className="w-1/5">
                <ComboboxDemo
                  key={`space-${!!getValues("space")}`}
                  name="space"
                  emptyPlaceholder="Espaço não encontrado"
                  searchPlaceholder="Busque um espaço"
                  selectPlaceholder="Selecionar espaço"
                  onChange={debounce(handleSearchSpaces, 500)}
                  items={spaces}
                  onSelect={handleSelectSpace}
                  shouldFilter={false}
                  defaultValue={getValues("space")}
                  customIcon={<ChevronDownIcon className={`h-4 w-4`} />}
                />
              </div>

              <div className="w-1/5">
                <Controller
                  name="poContractId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      id="po_contract_id"
                      name="poContractId"
                      value={field.value ?? ""}
                      className="shadow-none ring-gray-200 placeholder:text-black"
                      type="number"
                      placeholder="ID do contrato"
                      onWheel={(e) => e.currentTarget.blur()}
                      onBlur={() => handleSubmit(onSubmit)()}
                      error={fieldState.error ? fieldState.error.message : ""}
                    />
                  )}
                />
              </div>

              <div className="w-1/5">
                <ComboboxDemo
                  key={`company-${!!getValues("company")}`}
                  name="company"
                  emptyPlaceholder="Empresa não encontrada"
                  searchPlaceholder="Busque uma empresa"
                  selectPlaceholder="Selecionar empresa"
                  onChange={debounce(handleSearchCompanies, 500)}
                  items={companies}
                  onSelect={handleSelectCompany}
                  shouldFilter={false}
                  defaultValue={getValues("company")}
                  customIcon={<ChevronDownIcon className={`h-4 w-4`} />}
                  minSearchLength={3}
                />
              </div>

              <div className="w-1/5">
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <SearchInputCommand
                      {...field}
                      name="status"
                      chevron={true}
                      value={field.value?.name || ""}
                      searchDisable={true}
                      selectOption={(_, item) => {
                        if (!Array.isArray(item)) {
                          handleSelectStatus(item);
                        }
                      }}
                      onChange={(event) => {
                        field.onChange({ name: event.target.value });
                      }}
                      searchValue={field.value?.name || ""}
                      optionsList={statusOptions}
                      label="Selecionar status"
                      clear={!field.value?.id}
                      inputWidth="w-full"
                    />
                  )}
                />
              </div>

              <div className="w-1/5">
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      id="start_date"
                      name="startDate"
                      type="date"
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event);
                        handleSelectPeriod("startDate", event.target.value);
                      }}
                      error={fieldState.error ? fieldState.error.message : ""}
                      required
                    />
                  )}
                />
              </div>

              <div className="w-1/5">
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      id="end_date"
                      name="endDate"
                      type="date"
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event);
                        handleSelectPeriod("endDate", event.target.value);
                      }}
                      error={fieldState.error ? fieldState.error.message : ""}
                      required
                    />
                  )}
                />
              </div>

              <div className="gap-2 flex">
                <Button
                  disabled={isLoading}
                  variant="outline"
                  type="button"
                  onClick={clearFilters}
                >
                  Limpar filtros
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
